import React from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import Logo from '../components/ui/Logo';
import QuickCommerceLogo from '../components/ui/QuickCommerceLogo';
import QcPortalLoginForm from './login/QcPortalLoginForm';

const PLATFORMS = ['Zepto', 'Blinkit', 'Instamart'];

export default function LoginQc() {
  const { isDarkMode, toggleTheme } = useTheme();

  return (
    <div
      className={`min-h-screen relative overflow-hidden flex flex-col items-center justify-center px-4 py-12 transition-colors duration-500 ${
        isDarkMode ? 'bg-slate-950' : 'bg-gradient-to-br from-orange-50 via-amber-50 to-stone-100'
      }`}
    >
      <div className="absolute inset-0 pointer-events-none">
        {isDarkMode ? (
          <>
            <div className="absolute top-0 right-1/4 w-80 h-80 bg-orange-500/10 rounded-full blur-3xl" />
            <div className="absolute bottom-0 left-1/4 w-96 h-96 bg-amber-500/10 rounded-full blur-3xl" />
          </>
        ) : (
          <>
            <div className="absolute top-10 left-1/3 w-72 h-72 bg-orange-200/50 rounded-full blur-3xl" />
            <div className="absolute bottom-10 right-1/4 w-80 h-80 bg-amber-200/40 rounded-full blur-3xl" />
          </>
        )}
        <div
          className="absolute inset-0 opacity-40"
          style={{
            backgroundImage:
              'radial-gradient(circle at 1px 1px, rgb(234 88 12 / 0.08) 1px, transparent 0)',
            backgroundSize: '32px 32px',
          }}
        />
      </div>

      <button
        type="button"
        onClick={toggleTheme}
        className={`fixed top-6 right-6 z-50 p-3 rounded-xl transition-all ${
          isDarkMode
            ? 'bg-white/10 hover:bg-white/20 border border-white/10'
            : 'bg-white hover:bg-slate-50 border border-slate-200 shadow-lg'
        }`}
        title="Toggle theme"
      >
        {isDarkMode ? (
          <svg className="w-5 h-5 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-slate-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        )}
      </button>

      <div className="relative z-10 w-full max-w-md">
        <div className="flex justify-center mb-8">
          <div className="relative">
            <div
              className={`absolute -inset-2 rounded-2xl blur-lg ${
                isDarkMode ? 'bg-orange-500/20' : 'bg-orange-300/40'
              }`}
            />
            <Logo size="lg" isDarkMode={isDarkMode} className="relative" />
          </div>
        </div>

        <div className="relative">
          <div
            className={`absolute -inset-1 rounded-3xl blur-xl ${
              isDarkMode ? 'bg-orange-500/15' : 'bg-orange-200/50'
            }`}
          />
          <div
            className={`relative rounded-3xl border shadow-2xl overflow-hidden ${
              isDarkMode ? 'border-slate-700 bg-slate-900/90' : 'border-slate-200 bg-white'
            }`}
          >
            <div className="h-1 bg-gradient-to-r from-orange-400 via-amber-500 to-orange-600" />

            <div className="px-8 pt-8 pb-2">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div
                  className={`p-2.5 rounded-xl ${
                    isDarkMode ? 'bg-orange-500/15 border border-orange-500/25' : 'bg-orange-50 border border-orange-100'
                  }`}
                >
                  <QuickCommerceLogo size={36} />
                </div>
              </div>
              <h1
                className={`text-center text-2xl font-semibold tracking-tight ${
                  isDarkMode ? 'text-white' : 'text-slate-900'
                }`}
              >
                Quick Commerce
              </h1>
              <p className={`text-center text-sm mt-2 ${isDarkMode ? 'text-slate-400' : 'text-slate-600'}`}>
                Portal email, password &amp; Zepto OTP
              </p>

              <div className="flex flex-wrap justify-center gap-2 mt-5">
                {PLATFORMS.map((name) => (
                  <span
                    key={name}
                    className={`text-[11px] font-medium px-3 py-1 rounded-full border ${
                      isDarkMode
                        ? 'border-orange-500/30 text-orange-200/90 bg-orange-500/10'
                        : 'border-orange-200 text-orange-800 bg-orange-50'
                    }`}
                  >
                    {name}
                  </span>
                ))}
              </div>
            </div>

            <div className="px-8 pb-8 pt-4">
              <QcPortalLoginForm />
            </div>
          </div>
        </div>

        <p className={`text-center text-sm mt-8 ${isDarkMode ? 'text-slate-500' : 'text-slate-600'}`}>
          Sellerclub admin or Amazon?{' '}
          <Link
            to="/login"
            className="font-semibold text-orange-600 hover:text-orange-500 dark:text-orange-400"
          >
            Main login →
          </Link>
        </p>

        <p
          className={`text-center text-[11px] mt-4 uppercase tracking-wider ${
            isDarkMode ? 'text-slate-600' : 'text-slate-400'
          }`}
        >
          Secure portal verification
        </p>
      </div>
    </div>
  );
}
